import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import gql from 'graphql-tag';
import {MultiSelectType, tagFrag} from '../../components/multi-select/multi-select.component';
import {MultiSelectComponentTagFragment} from '../../components/multi-select/__generated__/MultiSelectComponentTagFragment';
import {catchError, EMPTY, Subject, Subscription, switchMap} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Apollo} from 'apollo-angular';
import {ErrorService} from '../../services/error.service';
import {EditTemplateTagsGet} from './__generated__/EditTemplateTagsGet';
import {ToastrService} from 'ngx-toastr';
import {EditTemplateTags, EditTemplateTagsVariables} from './__generated__/EditTemplateTags';

const editTemplateTagsGetQuery = gql`
    query EditTemplateTagsGet {
      tags {
        edges {
          node {
            ...MultiSelectComponentTagFragment
          }
        }
      }
    }
    ${tagFrag}
`;

const editTemplateTagsMutation = gql`
    mutation EditTemplateTags($id: ID, $tags: [ID]) {
      updateOrCreateBuilderTemplate(input: {id: $id, tags: $tags}) {
        templateItem {
          id
          tags {
            edges {
              node {
                ...MultiSelectComponentTagFragment
              }
            }
          }
        }
        error
      }
    }
    ${tagFrag}
`;

@Component({
  selector: 'app-edit-template-tags',
  templateUrl: './edit-template-tags.component.html',
  styleUrls: ['./edit-template-tags.component.scss'],
  standalone: false,
})
export class EditTemplateTagsComponent implements OnInit, OnDestroy {
  @Output() activeTags: EventEmitter<MultiSelectComponentTagFragment[]> = new EventEmitter();
  templateItemId = '';
  currentTags: MultiSelectComponentTagFragment[] = [];
  tags: MultiSelectComponentTagFragment[] = [];
  tagControl = new UntypedFormControl();
  MultiSelectType = MultiSelectType;
  saveTags$ = new Subject<MultiSelectComponentTagFragment[]>();
  private subscriptions: Subscription[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private errorService: ErrorService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.apollo
        .watchQuery<EditTemplateTagsGet>({
          query: editTemplateTagsGetQuery,
        })
        .valueChanges.pipe(
          catchError(err => {
            this.errorService.handleDefault(err);
            return EMPTY;
          }),
        )
        .subscribe(output => {
          this.tags = (output.data?.tags.edges ?? []).map(e => e.node);
          this.tagControl.setValue(this.currentTags);
        }),
      this.saveTags$
        .pipe(
          switchMap(tags => {
            return this.apollo
              .mutate<EditTemplateTags, EditTemplateTagsVariables>({
                mutation: editTemplateTagsMutation,
                variables: {
                  id: this.templateItemId,
                  tags: tags.map(t => t.id),
                },
              })
              .pipe(
                catchError(err => {
                  this.errorService.handleDefault(err);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(output => {
          this.toastr.success($localize`Successfully saved`);
          this.activeTags.emit(this.tagControl.value);
          this.closeModal();
        }),
    );
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  saveTags(): void {
    this.saveTags$.next(this.tagControl.value);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
